import { useState } from 'react';
import { Card, CardContent, IconButton, Button, Chip, FormControl, Select, MenuItem, Typography, Tooltip, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import MenuItemRow from './MenuItemRow';
import { getDeliveryDate, formatDate, toIsoDate, jsToWeekday } from './menuDateUtils';

const weekdayOptions = [
  { value: '', label: '-- Chọn --' },
  { value: 'Thứ 2', label: 'Thứ 2' },
  { value: 'Thứ 3', label: 'Thứ 3' },
  { value: 'Thứ 4', label: 'Thứ 4' },
  { value: 'Thứ 5', label: 'Thứ 5' },
  { value: 'Thứ 6', label: 'Thứ 6' },
  { value: 'Thứ 7', label: 'Thứ 7' },
  { value: 'Chủ nhật', label: 'Chủ nhật' },
];

const MenuDeliveryCard = ({ delivery, deliveryIndex, onChange, onRemove, products, cycleStartDate, weekIdx, totalWeeks, menuId, menuStatus, onRegenerateOrder }) => {
  const locked = delivery.has_orders === true;
  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerate = async () => {
    if (!onRegenerateOrder || regenerating) return;
    setRegenerating(true);
    try {
      await onRegenerateOrder(delivery.id);
    } finally {
      setRegenerating(false);
    }
  };
  const hasSpecificDate = !!delivery.delivery_date;
  const deliveryDate = hasSpecificDate
    ? new Date(delivery.delivery_date + 'T00:00:00')
    : getDeliveryDate(cycleStartDate, weekIdx, totalWeeks, delivery.delivery_day);
  const dateStr = formatDate(deliveryDate);
  const label = delivery.delivery_day
    ? `Lần ${deliveryIndex} — ${delivery.delivery_day}`
    : `Lần ${deliveryIndex}`;

  const handleDayChange = (day) => {
    const calculatedDate = getDeliveryDate(cycleStartDate, weekIdx, totalWeeks, day);
    onChange({ ...delivery, delivery_day: day, delivery_date: toIsoDate(calculatedDate), delivery_number: deliveryIndex });
  };

  const handleDateChange = (val) => {
    const dateStr = val ? val.format('YYYY-MM-DD') : null;
    const dayName = val ? jsToWeekday[val.day()] : delivery.delivery_day;
    onChange({ ...delivery, delivery_date: dateStr, delivery_day: dayName || delivery.delivery_day, delivery_number: deliveryIndex });
  };

  const handleItemChange = (itemIdx, updatedItem) => {
    const newItems = [...delivery.items];
    newItems[itemIdx] = updatedItem;
    onChange({ ...delivery, items: newItems });
  };

  const handleAddItem = () => {
    const newItem = {
      name: '', emoji: '🥬', detail: '', weight_text: '',
      weight_value: null, item_type: 'REGULAR', sort_order: delivery.items.length,
    };
    onChange({ ...delivery, items: [...delivery.items, newItem] });
  };

  const handleRemoveItem = (itemIdx) => {
    onChange({ ...delivery, items: delivery.items.filter((_, i) => i !== itemIdx) });
  };

  // Calculate total converted weight from items (exclude FREE_GIFT)
  const calcWeight = delivery.items.reduce((sum, item) => {
    if (item.item_type === 'FREE_GIFT') return sum;
    if (item.quantity && item.convert_rate) {
      return sum + (item.quantity / item.convert_rate);
    }
    if (item.weight_value) return sum + parseFloat(item.weight_value);
    return sum;
  }, 0);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px',
        backgroundColor: locked ? '#fff3e0' : '#e8f5e9',
        borderBottom: `1px solid ${locked ? '#ffe0b2' : '#c8e6c9'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {locked && (
            <Tooltip title="Lần giao này đã có đơn hàng, không thể chỉnh sửa">
              <LockIcon sx={{ fontSize: 18, color: '#e65100' }} />
            </Tooltip>
          )}
          <Typography sx={{ fontWeight: 600, minWidth: 120 }}>
            {label}
            {dateStr && (
              <Typography component="span" sx={{ ml: 1, fontSize: '0.8rem', color: hasSpecificDate ? 'primary.main' : 'text.secondary', fontWeight: hasSpecificDate ? 600 : 400 }}>
                ({dateStr})
              </Typography>
            )}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={delivery.delivery_day || ''}
              onChange={(e) => handleDayChange(e.target.value)}
              displayEmpty
              disabled={locked}
            >
              {weekdayOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <DatePicker
            value={delivery.delivery_date ? dayjs(delivery.delivery_date) : null}
            onChange={handleDateChange}
            disabled={locked}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 150 },
                placeholder: 'Ngay cu the',
              },
              field: { clearable: true },
            }}
            format="DD/MM/YYYY"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Chip
            label={`${Math.round(calcWeight * 100) / 100} KG`}
            color={locked ? 'warning' : 'success'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
          {!locked && menuStatus === 'ACTIVE' && delivery.id && delivery.delivery_day && onRegenerateOrder && (
            <Tooltip title="Tạo lại đơn hàng cho hôm nay">
              <Button
                size="small"
                startIcon={regenerating ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={handleRegenerate}
                disabled={regenerating}
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              >
                Tạo đơn
              </Button>
            </Tooltip>
          )}
          {!locked && (
            <IconButton size="small" onClick={onRemove} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      </div>
      <CardContent sx={{ p: '8px !important', opacity: locked ? 0.7 : 1 }}>
        {delivery.items.map((item, idx) => (
          <MenuItemRow
            key={idx}
            item={item}
            onChange={(updatedItem) => handleItemChange(idx, updatedItem)}
            onRemove={() => handleRemoveItem(idx)}
            products={products}
            disabled={locked}
          />
        ))}
        {!locked && (
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={handleAddItem}
            sx={{ mt: 1, ml: 1 }}
          >
            Thêm rau
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default MenuDeliveryCard;
